import React from "react";
import PropTypes from "prop-types";

const strengthText = (strengths) => (strengths || []).map((strength) => `${strength.value}${strength.unit || ""}`).join(" + ") || "—";
const brandText = (brandTokens) => (brandTokens || []).join(" ") || "—";

const FIELDS = [
    { key: "brand",    label: "Brand",   read: (identity) => brandText(identity.brandTokens) },
    { key: "strength", label: "Strength", read: (identity) => strengthText(identity.strengths) },
    { key: "form",     label: "Form",    read: (identity) => identity.form || "—" },
    { key: "release",  label: "Release", read: (identity) => identity.releaseType || "—" },
    { key: "variant",  label: "Variant", read: (identity) => identity.variant || "—" },
];

// Winner and alternatives name a source by pricingSource (EVITAL…); rejected samples by lookup key (evital…).
const SOURCE_LABEL = { EVITAL: "eVital", evital: "eVital", TATA1MG: "1mg", oneMg: "1mg", PRICE_MASTER: "PriceMaster", priceMaster: "PriceMaster" };
const sourceText = (entry) => {
    if (!entry.source) return "—";
    const label = SOURCE_LABEL[entry.source] || entry.source;
    return entry.vendor ? `${label} · ${entry.vendor}` : label;
};

const SALT_BADGE = {
    true:  { text: "salt ✓", className: "text-green-600" },
    false: { text: "salt ✗", className: "text-red-600" },
};

function SaltCell({ salt, saltMatch }) {
    const badge = SALT_BADGE[String(saltMatch)];
    return (
        <td className="pr-4 align-top text-gray-600 max-w-[220px]">
            {salt ? <div className="truncate" title={salt}>{salt}</div> : <span className="text-gray-300">—</span>}
            {badge && <span className={`text-[10px] font-semibold ${badge.className}`}>{badge.text}</span>}
        </td>
    );
}

SaltCell.propTypes = { salt: PropTypes.string, saltMatch: PropTypes.bool };
SaltCell.defaultProps = { salt: null, saltMatch: null };

const packText = (pack) => (pack?.quantity != null ? `${pack.quantity} units` : "—") + (pack?.raw ? ` (${pack.raw})` : "");

function Row({ label, tone, entry, query }) {
    const identity = entry.identity;
    return (
        <tr className={tone}>
            <td className="pl-2 pr-4 py-1 align-top font-semibold text-gray-800 whitespace-nowrap">
                {label}
                {entry.name && <div className="font-normal text-gray-500 max-w-[220px] truncate">{entry.name}</div>}
            </td>
            <td className="pr-4 align-top text-gray-700 font-semibold whitespace-nowrap">{sourceText(entry)}</td>
            {FIELDS.map((field) => {
                if (!identity) return <td key={field.key} className="pr-4 align-top text-gray-300">—</td>;
                const value = field.read(identity);
                const isDifferent = query && value !== field.read(query);
                return (
                    <td key={field.key} className={`pr-4 align-top ${isDifferent ? "text-red-600 font-semibold" : "text-gray-700"}`}>{value}</td>
                );
            })}
            <SaltCell salt={entry.salt} saltMatch={entry.saltMatch} />
            <td className="pr-4 align-top text-gray-600">{entry.pack ? packText(entry.pack) : "—"}</td>
            <td className="pr-2 align-top text-gray-500">
                {entry.reason || (entry.perUnitPrice != null ? `₹${entry.perUnitPrice} / unit` : "")}
                {entry.matchLevel && (
                    <div className={`text-[10px] font-semibold ${entry.matchLevel === "EXACT" ? "text-green-600" : "text-amber-600"}`}>
                        {entry.matchLevel === "EXACT" ? "exact match" : `probable match${entry.nameDistance > 0 ? ` · name differs by ${entry.nameDistance}` : ""}`}
                    </div>
                )}
            </td>
        </tr>
    );
}

Row.propTypes = {
    label: PropTypes.string.isRequired,
    tone:  PropTypes.string,
    entry: PropTypes.object.isRequired,
    query: PropTypes.object,
};
Row.defaultProps = { tone: "", query: null };

function IdentityComparison({ funnel }) {
    if (!funnel?.query) return null;
    const winner = funnel.select?.winner;
    const alternatives = funnel.select?.alternatives || [];
    const rejected = funnel.gate?.rejectedSamples || [];

    return (
        <div className="mt-4">
            <p className="text-[10px] uppercase tracking-wide text-gray-400 font-sans font-semibold mb-1">
                Cart item vs supplier matches (differences in red)
            </p>
            <div className="overflow-x-auto">
                <table className="w-full text-xs font-sans border-separate border-spacing-y-1">
                    <thead>
                        <tr className="text-gray-400 text-[10px] uppercase tracking-wide text-left">
                            <th className="pl-2 pr-4">Item</th>
                            <th className="pr-4">Source</th>
                            {FIELDS.map((field) => <th key={field.key} className="pr-4">{field.label}</th>)}
                            <th className="pr-4">Salt</th>
                            <th className="pr-4">Pack</th>
                            <th className="pr-2">Price / reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        <Row label="Cart item" tone="bg-blue-50" entry={{ name: funnel.query.itemName, identity: funnel.query, salt: funnel.query.salt }} />
                        {winner && <Row label="Winner" tone="bg-green-50" entry={winner} query={funnel.query} />}
                        {alternatives.map((alternative, i) => (
                            <Row key={`alt-${i}`} label="Also passed" tone="bg-gray-50" entry={alternative} query={funnel.query} />
                        ))}
                        {rejected.map((sample, i) => (
                            <Row key={`rej-${i}`} label="Rejected" tone="bg-red-50" entry={sample} query={funnel.query} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

IdentityComparison.propTypes = { funnel: PropTypes.object };
IdentityComparison.defaultProps = { funnel: null };

export default IdentityComparison;
